import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import { telegramEscopoHint, telegramNaoRecebe, PAPEIS_QUE_RECEBEM as PAPEIS_OK,
         MOSTRAR_FREQUENCIA, MODO_PADRAO } from '../utils/telegramEscopo';

/**
 * Bloco de Telegram do cadastro de usuário: vínculo (convite/desvincular) + o que a pessoa
 * recebe. Usado por Editar Usuário (vendedor) e Editar Administrador (chefia) — as duas telas
 * existem porque vendedor e chefia são cadastrados por caminhos diferentes no admin.
 *
 * Extraído em componente depois de o bloco ter nascido só em UserEdit: os diretores, gerentes
 * e supervisores vivem em /users/edit-admin, e a feature é sobre eles. Manter duas (agora
 * três) cópias disso à mão era garantia de divergir.
 *
 * O componente é dono do VÍNCULO (fala com a API sozinho: convite, status, desvincular) e
 * recebe as PREFERÊNCIAS como props controladas, porque quem submete o formulário é a tela.
 */
/**
 * Formata um timestamp da API no fuso de Brasília.
 *
 * O banco guarda `timestamp without time zone` em UTC (o TimeZone do RDS é UTC) e a API
 * devolve o isoformat CRU, sem sufixo — ex.: "2026-09-09T11:23:29.461045". O JavaScript, por
 * especificação, trata data-hora SEM timezone como hora LOCAL, então aquele valor aparecia
 * como 11:23 quando o vínculo aconteceu às 08:23 de Brasília. Reportado pelo CS em 09/09.
 *
 * Daí as duas metades do conserto: marcar o valor como UTC (o 'Z') para o parse não inventar
 * fuso, e formatar explicitamente em America/Sao_Paulo em vez de confiar no fuso do navegador
 * — que muda se o admin estiver viajando.
 */
export const dataHoraBR = (valor) => {
    if (!valor) return '';
    const temFuso = /(Z|[+-]\d{2}:?\d{2})$/.test(valor);
    const d = new Date(temFuso ? valor : valor + 'Z');
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

const TelegramFeedbackFields = ({
    userId, role,
    username, onUsernameChange,
    enabled, onEnabledChange,
    short, onShortChange,
    long, onLongChange,
    // Estado do vínculo que a tela já tinha em mão (foto da listagem); serve de valor inicial
    // até o /telegram/status responder.
    linkedInicial, linkedAtInicial,
    // Nas telas de CRIAÇÃO não existe usuário ainda: não há a quem convidar nem status a
    // consultar. Só as preferências aparecem; o convite fica para a edição, depois de salvar.
    mostrarVinculo = true,
}) => {
    const [vinculoAoVivo, setVinculoAoVivo] = useState(null);
    const [convite, setConvite] = useState(null);
    const [gerando, setGerando] = useState(false);
    const [copiado, setCopiado] = useState(false);
    const [erro, setErro] = useState('');
    const [confirmando, setConfirmando] = useState(false);
    const [desvinculando, setDesvinculando] = useState(false);

    const vinculado = vinculoAoVivo ? vinculoAoVivo.linked : Boolean(linkedInicial);
    const vinculadoEm = vinculoAoVivo ? vinculoAoVivo.linked_at : linkedAtInicial;

    // Ao ABRIR: o objeto da listagem é uma foto, e o /start acontece fora desta tela.
    useEffect(() => {
        if (!userId || !mostrarVinculo) return undefined;
        let cancelado = false;
        (async () => {
            const res = await apiService.telegramStatus(userId);
            if (cancelado || !res.success || !res.data) return;
            setVinculoAoVivo(res.data);
            if (res.data.telegram_username) onUsernameChange(res.data.telegram_username);
            if (typeof res.data.feedback_telegram_enabled === 'boolean') onEnabledChange(res.data.feedback_telegram_enabled);
            if (typeof res.data.feedback_telegram_short === 'boolean') onShortChange(res.data.feedback_telegram_short);
            if (typeof res.data.feedback_telegram_long === 'boolean') onLongChange(res.data.feedback_telegram_long);
        })();
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    // Enquanto o convite está na tela, pergunta a cada 4s se o /start já aconteceu.
    useEffect(() => {
        if (!convite || !userId) return undefined;
        let cancelado = false, tentativas = 0, timer = null;
        const bater = async () => {
            tentativas += 1;
            const res = await apiService.telegramStatus(userId);
            if (cancelado) return;
            if (res.success && res.data && res.data.linked) {
                setVinculoAoVivo(res.data);
                if (!username && res.data.telegram_username) onUsernameChange(res.data.telegram_username);
                if (timer) clearInterval(timer);
            } else if (tentativas >= 45 && timer) {
                clearInterval(timer);
            }
        };
        timer = setInterval(bater, 4000);
        bater();
        return () => { cancelado = true; if (timer) clearInterval(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [convite, userId]);

    const gerarConvite = async () => {
        setErro(''); setCopiado(false); setGerando(true);
        try {
            const res = await apiService.telegramLink(userId);
            if (res.success) setConvite(res.data);
            else setErro(res.message || 'Não foi possível gerar o convite.');
        } finally { setGerando(false); }
    };

    const copiar = async () => {
        if (!convite?.link) return;
        try { await navigator.clipboard.writeText(convite.link); setCopiado(true); }
        catch (e) { setErro('Não consegui copiar. Selecione o link e copie manualmente.'); }
    };

    const desvincular = async () => {
        setErro(''); setDesvinculando(true);
        try {
            const res = await apiService.telegramUnlink(userId);
            if (res.success) {
                setVinculoAoVivo({ linked: false, linked_at: null, telegram_username: null });
                onUsernameChange('');
                onEnabledChange(false);
                onLongChange(false);
                setConvite(null);
                setConfirmando(false);
            } else {
                setErro(res.message || 'Não foi possível desvincular.');
            }
        } finally { setDesvinculando(false); }
    };

    return (
        <>
            {mostrarVinculo ? (
            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="telegramUsername">Telegram (@usuário) <span className="field-optional">(opcional)</span></label>
                    <input
                        id="telegramUsername"
                        type="text"
                        value={username}
                        onChange={(e) => onUsernameChange(e.target.value)}
                        className="form-input"
                        placeholder="@maria.silva"
                    />
                    <small className="field-hint">
                        {!vinculado
                            ? 'Ainda não vinculado — gere o convite ao lado.'
                            : `Vinculado${vinculadoEm ? ' em ' + dataHoraBR(vinculadoEm) : ''}.`}
                        {/* @username é OPCIONAL no Telegram e muita gente não tem. Sem esta
                            frase, o campo vazio parecia falha de captura — foi o que o CS
                            reportou em 09/09 (o Alex está vinculado e não tem @usuário). */}
                        {vinculado && !username && (
                            <> Esta pessoa não tem <strong>@usuário</strong> no Telegram — é
                            opcional lá, e o vínculo funciona sem ele.</>
                        )}
                    </small>
                </div>
                <div className="form-group">
                    <label>Convite do Telegram</label>
                    <button type="button" className="btn btn-cancel" onClick={gerarConvite} disabled={gerando}>
                        {gerando ? 'Gerando...' : (vinculado ? 'Gerar novo convite' : 'Gerar convite')}
                    </button>
                    {vinculado && (
                        confirmando ? (
                            <div className="telegram-desvincular">
                                <small className="field-hint">
                                    Desvincular apaga o vínculo, <strong>desliga a entrega</strong> e invalida
                                    convites pendentes. A pessoa é avisada no chat.
                                </small>
                                <div className="telegram-desvincular-acoes">
                                    <button type="button" className="btn btn-cancel" onClick={() => setConfirmando(false)} disabled={desvinculando}>Manter</button>
                                    <button type="button" className="btn btn-danger" onClick={desvincular} disabled={desvinculando}>
                                        {desvinculando ? 'Desvinculando...' : 'Confirmar'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button type="button" className="btn btn-cancel" onClick={() => setConfirmando(true)} style={{ marginTop: 6 }}>
                                Desvincular Telegram
                            </button>
                        )
                    )}
                    {erro && <small className="field-hint" style={{ color: '#c0392b' }}>{erro}</small>}
                    {convite && (
                        <div className="telegram-convite">
                            <input type="text" readOnly value={convite.link} className="form-input" onFocus={(e) => e.target.select()} />
                            <button type="button" className="btn btn-cancel" onClick={copiar}>
                                {copiado ? 'Copiado!' : 'Copiar link'}
                            </button>
                            {vinculoAoVivo && vinculoAoVivo.linked ? (
                                <small className="field-hint" style={{ color: '#1e8449', fontWeight: 600 }}>
                                    ✅ Vinculado! {vinculoAoVivo.telegram_username ? `(@${vinculoAoVivo.telegram_username})` : ''}
                                </small>
                            ) : (
                                <small className="field-hint">
                                    Mande no WhatsApp da pessoa. Vale <strong>{convite.expires_in_hours}h</strong> e
                                    serve <strong>uma vez só</strong> — quem abrir o link passa a receber os feedbacks
                                    deste usuário, então não repasse para grupo.
                                    <br />Esta tela avisa aqui quando a pessoa der /start.
                                </small>
                            )}
                        </div>
                    )}
                </div>
            </div>
            ) : (
            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="telegramUsername">Telegram (@usuário) <span className="field-optional">(opcional)</span></label>
                    <input
                        id="telegramUsername"
                        type="text"
                        value={username}
                        onChange={(e) => onUsernameChange(e.target.value)}
                        className="form-input"
                        placeholder="@maria.silva"
                    />
                    <small className="field-hint">
                        Só para conferência. O vínculo é feito pelo convite, na edição depois de salvar.
                    </small>
                </div>
                <div className="form-group" />
            </div>
            )}

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="feedbackTelegramEnabled">Receber Feedbacks por Telegram?</label>
                    <select
                        id="feedbackTelegramEnabled"
                        value={enabled ? 'true' : 'false'}
                        onChange={(e) => onEnabledChange(e.target.value === 'true')}
                        className="form-input"
                    >
                        <option value="false">Não</option>
                        <option value="true">Sim</option>
                    </select>
                    {enabled && !vinculado && (
                        <small className="field-hint">Sem vínculo não há entrega: gere o convite e peça o /start.</small>
                    )}
                    {/* Um papel fora da regra é ESTADO QUE NUNCA ENTREGA, não informação
                        neutra — por isso vermelho, e não a mesma dica cinza dos outros. */}
                    {enabled && role && !PAPEIS_OK.includes(role) ? (
                        <small className="field-hint" style={{ color: '#c0392b', fontWeight: 600 }}>
                            ⚠️ {telegramNaoRecebe(role)}
                        </small>
                    ) : enabled && !MOSTRAR_FREQUENCIA && role ? (
                        <small className="field-hint">{telegramEscopoHint(role, MODO_PADRAO)}</small>
                    ) : null}
                </div>
                <div className="form-group">
                    <label>O que a pessoa recebe</label>
                    <div className={`telegram-tipos${enabled ? '' : ' telegram-tipos-off'}`}>
                        <label className="telegram-tipo">
                            <input type="checkbox" checked={short} disabled={!enabled} onChange={(e) => onShortChange(e.target.checked)} />
                            <span>Feedback curto</span>
                        </label>
                        <label className="telegram-tipo">
                            <input type="checkbox" checked={long} disabled={!enabled} onChange={(e) => onLongChange(e.target.checked)} />
                            <span>Feedback longo</span>
                        </label>
                    </div>
                    {!enabled ? (
                        <small className="field-hint">Ligue &quot;Receber Feedbacks por Telegram&quot; ao lado para escolher.</small>
                    ) : (role && !PAPEIS_OK.includes(role)) ? (
                        <small className="field-hint" style={{ color: '#c0392b' }}>
                            Não faz diferença marcar: este perfil não recebe.
                        </small>
                    ) : (!short && !long) ? (
                        <small className="field-hint" style={{ color: '#c0392b' }}>Sem marcar nenhum, a pessoa não recebe nada.</small>
                    ) : long ? (
                        <small className="field-hint">
                            O feedback longo só é gerado para alguns clientes — onde não houver, simplesmente não sai nada.
                        </small>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default TelegramFeedbackFields;
